import React, { ReactNode } from 'react';

import {
  FolderDTO,
  FolderType,
  LocationDTO,
  OrganizationStructureDTO,
  ParentStructType,
  SuborganizationDTO
} from '@/api/generated';

import LocationItem from './LocationItem';
import { ItemMap, RecursiveItem, RecursiveOrganizationStructure, SimpleItem } from './types';

export interface TreeNode {
  key?: string;
  label?: string | ReactNode;
  parentKey?: string;
  type?: FolderType;
  children?: TreeNode[];
  selectable?: boolean;
  data: object;
}

interface TreeValue {
  checked: boolean;
  partialChecked: boolean;
}

export const buildTreeNodeOptions = (
  structure: RecursiveOrganizationStructure,
  includeRoot: boolean = false,
  allowedTypes: FolderType[] | undefined = undefined
): TreeNode[] => {
  try {
    const convertChildItem =
      (parentKey: string) =>
      (item: RecursiveItem): TreeNode | null => {
        if (!item || !item.slug) return null;
        const currentKey = item.slug;

        return {
          key: currentKey,
          label: <LocationItem type={item.type} name={item.name} />,
          parentKey,
          children: (item.children || [])
            .map(convertChildItem(currentKey))
            .filter((item): item is TreeNode => Boolean(item)),
          type: item.type,
          selectable: !allowedTypes || allowedTypes?.includes(item.type!),
          data: {
            key: currentKey
          }
        };
      };

    const children = (structure.children || [])
      .map(convertChildItem(structure.slug!))
      .filter((item): item is TreeNode => Boolean(item));

    return includeRoot
      ? [
          {
            key: structure.slug,
            label: <LocationItem type={structure.type} name={structure.name} />,
            children,
            type: structure.type,
            selectable: !allowedTypes || allowedTypes?.includes(structure.type!),
            data: {
              key: structure.slug
            }
          }
        ]
      : children;
  } catch (error) {
    // eslint-disable-next-line i18next/no-literal-string,no-console
    console.error('Failed to transform organization structure', error);

    return [];
  }
};

export const createNodeMap = (tree: TreeNode[]) => {
  const nodeMap: { [key: string]: TreeNode } = {};

  const mapNodes = (node: TreeNode) => {
    if (node?.key) nodeMap[node.key] = node;
    node?.children?.forEach(mapNodes);
  };

  tree?.forEach(mapNodes);

  return nodeMap;
};

export const createLocationTreeValue = (
  keysList: string[],
  nodeMap: { [key: string]: TreeNode }
): { [key: string]: TreeValue } => {
  const treeValue: { [key: string]: TreeValue } = {};

  keysList?.forEach(key => {
    treeValue[key] = { checked: true, partialChecked: false };
  });

  keysList?.forEach(key => {
    const node = nodeMap[key];

    let currentNode = node;
    while (currentNode?.parentKey) {
      const parentNode = nodeMap[currentNode?.parentKey];
      if (parentNode?.key && !treeValue[parentNode.key]) {
        treeValue[parentNode.key!] = { checked: false, partialChecked: true };
      }
      currentNode = parentNode;
    }
  });

  return treeValue;
};

export const buildRecursiveStructure = (orgStructure: OrganizationStructureDTO): RecursiveOrganizationStructure => {
  const result: RecursiveOrganizationStructure = {
    name: orgStructure.name || '',
    slug: orgStructure.slug || '',
    type: FolderType.ORGANIZATION,
    children: []
  };

  const itemMap: ItemMap = {};

  const allItems: (FolderDTO & SuborganizationDTO)[] = [
    ...(orgStructure.folders || []).map(item => ({
      ...item,
      type: FolderType.FOLDER
    })),
    ...(orgStructure.subFolders || []).map(item => ({
      ...item,
      type: FolderType.SUB_FOLDER
    })),
    ...(orgStructure.subOrganizations || []).map(item => ({
      ...item,
      type: FolderType.SUB_ORGANIZATION
    }))
  ];

  allItems.forEach(item => {
    itemMap[item.slug || ''] = {
      slug: item.slug || '',
      name: item.name || '',
      type: item.type!,
      children: [],
      parentSlug: item?.parent?.slug,
      parentType: item?.parent?.type,
      status: item.status
    };
  });

  allItems.forEach(item => {
    const itemSlug = item.slug || '';
    const itemNode = itemMap[itemSlug];
    const parentItem = item.parent && itemMap[item.parent.slug || ''];

    const isDirectRootChild =
      !parentItem || item?.parent?.slug === orgStructure.slug || item?.parent?.type === ParentStructType.ORGANIZATION;

    if (isDirectRootChild) {
      result.children = result.children || [];
      result.children.push(itemNode);
    } else if (parentItem) {
      parentItem.children = parentItem.children || [];
      parentItem.children.push(itemNode);
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Item ${itemSlug} has no parent`);
    }
  });

  return result;
};

export const buildFlatStructure = (orgStructure: OrganizationStructureDTO): SimpleItem[] => [
  {
    slug: orgStructure.slug || '',
    name: orgStructure.name || '',
    type: FolderType.ORGANIZATION,
    status: orgStructure.status
  },
  ...(orgStructure.folders || []).map(item => ({
    slug: item.slug || '',
    name: item.name || '',
    type: FolderType.FOLDER
  })),
  ...(orgStructure.subFolders || []).map(item => ({
    slug: item.slug || '',
    name: item.name || '',
    type: FolderType.SUB_FOLDER
  })),
  ...(orgStructure.subOrganizations || []).map(item => ({
    slug: item.slug || '',
    name: item.name || '',
    type: FolderType.SUB_ORGANIZATION
  }))
];

export const buildMapStructure = (orgStructure: RecursiveOrganizationStructure) => {
  const nodeMap: { [key: string]: RecursiveItem } = {};

  const mapNodes = (node: RecursiveItem) => {
    if (node?.slug) nodeMap[node.slug] = node;
    node?.children?.forEach(mapNodes);
  };

  [orgStructure]?.forEach(mapNodes);

  return nodeMap;
};

export const buildPathToLocation = (locationMap: { [key: string]: RecursiveItem }, locationSlug?: string) => {
  if (!locationSlug || !locationMap[locationSlug]) return [];

  const path: RecursiveItem[] = [];
  let currentNode: RecursiveItem | null = locationMap[locationSlug];

  while (currentNode) {
    path.unshift(currentNode);
    currentNode = currentNode.parentSlug ? locationMap[currentNode.parentSlug] : null;
  }

  return path;
};

export const getRelevantParentFolder = (
  item: RecursiveItem,
  mapStructure: {
    [key: string]: RecursiveItem;
  }
) => {
  const usedSlugsSet = new Set();
  let currentItem = item;

  while (currentItem && currentItem?.type === FolderType.SUB_FOLDER && !usedSlugsSet?.has(currentItem?.slug)) {
    usedSlugsSet.add(currentItem?.slug);
    currentItem = mapStructure?.[currentItem.parentSlug!];
  }

  return currentItem?.type === FolderType.FOLDER ? currentItem : null;
};

export const getFolderSlugForLocation = (
  location: LocationDTO | undefined,
  mapStructure: { [key: string]: RecursiveItem } | undefined
): string | undefined => {
  if (!location || !mapStructure) {
    return undefined;
  }

  if (location.type === FolderType.FOLDER) {
    return location.slug;
  }

  const item = mapStructure[location.slug!];
  if (!item) {
    return undefined;
  }
  const parentFolder = getRelevantParentFolder(item, mapStructure);

  return parentFolder?.slug;
};
